import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { questionsService } from '@/services/questions.service';
import { CheckCircle, XCircle, Edit3, ChevronLeft, ChevronRight, AlertCircle, X, Filter, GraduationCap, FileQuestion } from 'lucide-react';
import { ViewToggle } from '@/components/ViewToggle';
import { Breadcrumb } from '@/components/Breadcrumb';
import { EmptyState } from '@/components/EmptyState';
import { Tooltip } from '@/components/Tooltip';
import { StatusBadge, QualityBadge } from '@/components/Badge';
import { storage } from '@/utils/localStorage';
import { useToast } from '@/contexts/ToastContext';
import type { Question } from '@/types';

export function ReviewQueue() {
  const { showToast } = useToast();

  // Load preferences from localStorage
  const savedExamFilter = storage.getFormValue<string>('reviewQueue', 'examFilter') || 'all';
  const savedTypeFilter = storage.getFormValue<string>('reviewQueue', 'typeFilter') || 'all';

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedQuestion, setEditedQuestion] = useState<Question | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionFeedback, setRejectionFeedback] = useState('');
  const [examFilter, setExamFilter] = useState<string>(savedExamFilter);
  const [typeFilter, setTypeFilter] = useState<string>(savedTypeFilter);
  const [view, setView] = useState<'grid' | 'list'>('list');
  const queryClient = useQueryClient();

  const { data: allQuestions, isLoading } = useQuery({
    queryKey: ['review-queue'],
    queryFn: async () => {
      try {
        const result = await questionsService.getQuestions({ status: 'under_review' });
        // Extract questions array from response
        return result?.data?.questions || [];
      } catch (error) {
        console.error('Failed to fetch review queue:', error);
        return [];
      }
    },
  });

  // Filter questions based on exam and type
  const questions = useMemo(() => {
    if (!allQuestions) return [];
    return allQuestions.filter((q: any) => {
      const matchesExam = examFilter === 'all' || q.exam_id === examFilter;
      const matchesType = typeFilter === 'all' || q.question_type === typeFilter;
      return matchesExam && matchesType;
    });
  }, [allQuestions, examFilter, typeFilter]);

  const currentQuestion = questions?.[currentIndex];

  // Save filter preferences to localStorage
  useEffect(() => {
    storage.rememberFormValue('reviewQueue', 'examFilter', examFilter);
  }, [examFilter]);

  useEffect(() => {
    storage.rememberFormValue('reviewQueue', 'typeFilter', typeFilter);
  }, [typeFilter]);

  // Auto-save draft when editing
  useEffect(() => {
    if (isEditing && editedQuestion) {
      const saveTimeout = setTimeout(() => {
        storage.saveDraft('question', editedQuestion.question_id, editedQuestion);
      }, 2000); // Auto-save after 2 seconds of no changes

      return () => clearTimeout(saveTimeout);
    }
  }, [isEditing, editedQuestion]);

  // Load draft when starting to edit
  useEffect(() => {
    if (currentQuestion && isEditing) {
      const draft = storage.getDraft<Question>('question', currentQuestion.question_id);
      if (draft) {
        setEditedQuestion(draft.data);
      }
    }
  }, [isEditing, currentQuestion]);

  // Track recently viewed questions
  useEffect(() => {
    if (currentQuestion) {
      storage.addRecentlyViewed(
        'question',
        currentQuestion.question_id,
        `${currentQuestion.exam_id} - ${currentQuestion.question_text.substring(0, 50)}...`
      );
    }
  }, [currentQuestion?.question_id]);

  // Get unique exams and types for filters
  const exams = useMemo(() => {
    if (!allQuestions) return [];
    return Array.from(new Set(allQuestions.map((q: any) => q.exam_id)));
  }, [allQuestions]);

  const questionTypes = useMemo(() => {
    if (!allQuestions) return [];
    return Array.from(new Set(allQuestions.map((q: any) => q.question_type)));
  }, [allQuestions]);

  // Stats by exam and type
  const stats = useMemo(() => {
    if (!allQuestions) return { byExam: {}, byType: {}, total: 0 };

    const byExam: Record<string, number> = {};
    const byType: Record<string, number> = {};

    allQuestions.forEach((q: any) => {
      byExam[q.exam_id] = (byExam[q.exam_id] || 0) + 1;
      byType[q.question_type] = (byType[q.question_type] || 0) + 1;
    });

    return {
      byExam,
      byType,
      total: allQuestions.length
    };
  }, [allQuestions]);

  const approveMutation = useMutation({
    mutationFn: async (questionId: string) => {
      return await questionsService.approveQuestion(questionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      // Clear draft after approval
      if (currentQuestion) {
        storage.clearDraft('question', currentQuestion.question_id);
      }
      showToast('success', 'Question Approved!', 'Question has been added to the question bank.');
      moveToNext();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ questionId, feedback }: { questionId: string; feedback: string }) => {
      return await questionsService.rejectQuestion(questionId, feedback);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      // Clear draft after rejection
      if (currentQuestion) {
        storage.clearDraft('question', currentQuestion.question_id);
      }
      setShowRejectModal(false);
      setRejectionFeedback('');
      showToast('info', 'Question Rejected', 'Feedback has been sent to RAG system for learning.');
      moveToNext();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (question: Question) => {
      return await questionsService.updateQuestion(question.question_id, question as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-queue'] });
      // Clear draft after successful save
      if (editedQuestion) {
        storage.clearDraft('question', editedQuestion.question_id);
      }
      setIsEditing(false);
      setEditedQuestion(null);
    },
  });

  useEffect(() => {
    if (currentQuestion) {
      setEditedQuestion(currentQuestion);
    }
  }, [currentQuestion]);

  const handleApprove = () => {
    if (currentQuestion) {
      approveMutation.mutate(currentQuestion.question_id);
    }
  };

  const handleRejectClick = () => {
    setShowRejectModal(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectionFeedback.trim()) {
      showToast('warning', 'Feedback Required', 'Please provide feedback for rejection.');
      return;
    }
    if (currentQuestion) {
      rejectMutation.mutate({
        questionId: currentQuestion.question_id,
        feedback: rejectionFeedback
      });
    }
  };

  const handleResetFilters = () => {
    setExamFilter('all');
    setTypeFilter('all');
    setCurrentIndex(0);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (isEditing || showRejectModal) return;

      switch (e.key.toLowerCase()) {
        case 'a':
          handleApprove();
          break;
        case 'r':
          handleRejectClick();
          break;
        case 'e':
          setIsEditing(true);
          break;
        case 'arrowright':
          moveToNext();
          break;
        case 'arrowleft':
          moveToPrevious();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentQuestion, isEditing, showRejectModal]);

  const moveToNext = () => {
    if (questions && currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsEditing(false);
    }
  };

  const moveToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsEditing(false);
    }
  };

  const handleSaveEdit = () => {
    if (editedQuestion) {
      updateMutation.mutate(editedQuestion);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedQuestion(currentQuestion);
    // Clear draft on cancel
    if (currentQuestion) {
      storage.clearDraft('question', currentQuestion.question_id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-500">Loading review queue...</div>
      </div>
    );
  }

  // Empty state when no questions at all
  if (!allQuestions || allQuestions.length === 0) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={[{ label: 'Review Queue' }]} />
        <EmptyState
          icon={CheckCircle}
          title="All caught up!"
          description="No questions pending review. Check back later or generate new questions."
          action={{
            label: 'Generate Questions',
            to: '/generate'
          }}
        />
      </div>
    );
  }

  // Empty state when filters produce no results
  if (questions.length === 0) {
    return (
      <div className="space-y-6">
        <Breadcrumb items={[{ label: 'Review Queue' }]} />

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Review Queue</h1>
            <p className="text-gray-600 mt-1">
              {stats.total} questions pending review
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Tooltip content="Total questions awaiting review">
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Pending</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <FileQuestion className="w-10 h-10 text-blue-500" />
              </div>
            </div>
          </Tooltip>
          {Object.entries(stats.byExam).map(([exam, count]) => (
            <Tooltip key={exam} content={`${count} questions from ${exam}`}>
              <div className="card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{exam} Questions</p>
                    <p className="text-3xl font-bold text-gray-900">{count}</p>
                  </div>
                  <GraduationCap className="w-10 h-10 text-green-500" />
                </div>
              </div>
            </Tooltip>
          ))}
        </div>

        {/* Filters */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            </div>
            <ViewToggle view={view} onViewChange={setView} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Exam</label>
              <select
                value={examFilter}
                onChange={(e) => {
                  setExamFilter(e.target.value);
                  setCurrentIndex(0);
                }}
                className="input-field"
              >
                <option value="all">All Exams ({stats.total})</option>
                {exams.map((exam: any) => (
                  <option key={exam} value={exam as string}>{exam as string} ({(stats.byExam as any)[exam]})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Question Type</label>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCurrentIndex(0);
                }}
                className="input-field"
              >
                <option value="all">All Types</option>
                {questionTypes.map((type: any) => (
                  <option key={type} value={type as string}>{type as string} ({(stats.byType as any)[type]})</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={handleResetFilters}
                className="btn-secondary w-full"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        <EmptyState
          icon={Filter}
          title="No questions match your filters"
          description={`Try adjusting your filters to see more questions. ${stats.total} total questions are pending review.`}
          action={{
            label: 'Reset Filters',
            onClick: handleResetFilters
          }}
          secondaryAction={{
            label: 'View All Questions',
            to: '/questions'
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={[{ label: 'Review Queue' }]} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Review Queue</h1>
          <p className="text-gray-600 mt-1">
            {stats.total} questions pending review
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Tooltip content="Total questions awaiting review">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Pending</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <FileQuestion className="w-10 h-10 text-blue-500" />
            </div>
          </div>
        </Tooltip>
        {Object.entries(stats.byExam).map(([exam, count]) => (
          <Tooltip key={exam} content={`${count} questions from ${exam}`}>
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{exam} Questions</p>
                  <p className="text-3xl font-bold text-gray-900">{count}</p>
                </div>
                <GraduationCap className="w-10 h-10 text-green-500" />
              </div>
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>
          <ViewToggle view={view} onViewChange={setView} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Exam</label>
            <select
              value={examFilter}
              onChange={(e) => {
                setExamFilter(e.target.value);
                setCurrentIndex(0);
              }}
              className="input-field"
            >
              <option value="all">All Exams ({stats.total})</option>
              {exams.map((exam: any) => (
                <option key={exam} value={exam as string}>{exam as string} ({(stats.byExam as any)[exam]})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Question Type</label>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setCurrentIndex(0);
              }}
              className="input-field"
            >
              <option value="all">All Types</option>
              {questionTypes.map((type: any) => (
                <option key={type} value={type as string}>{type as string} ({(stats.byType as any)[type]})</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={handleResetFilters}
              className="btn-secondary w-full"
            >
              Reset Filters
            </button>
          </div>
        </div>
        {(examFilter !== 'all' || typeFilter !== 'all') && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900">
              Showing {questions.length} of {stats.total} questions
              {examFilter !== 'all' && ` for ${examFilter}`}
              {typeFilter !== 'all' && ` of type ${typeFilter}`}
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      {questions.length > 0 && (
        <div className="flex items-center justify-between card">
          <p className="text-sm text-gray-600">
            Question {currentIndex + 1} of {questions.length}
          </p>
          <div className="flex items-center space-x-2">
            <Tooltip content="Previous question (← arrow key)">
              <button
                onClick={moveToPrevious}
                disabled={currentIndex === 0}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            </Tooltip>
            <Tooltip content="Next question (→ arrow key)">
              <button
                onClick={moveToNext}
                disabled={currentIndex === questions.length - 1}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </Tooltip>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900 mb-1">Keyboard Shortcuts</p>
            <div className="flex flex-wrap gap-4 text-xs text-blue-700">
              <Tooltip content="Approve the current question and move to next">
                <span><kbd className="px-2 py-1 bg-white rounded border border-blue-300">A</kbd> Approve</span>
              </Tooltip>
              <Tooltip content="Reject the current question with feedback">
                <span><kbd className="px-2 py-1 bg-white rounded border border-blue-300">R</kbd> Reject</span>
              </Tooltip>
              <Tooltip content="Edit the current question">
                <span><kbd className="px-2 py-1 bg-white rounded border border-blue-300">E</kbd> Edit</span>
              </Tooltip>
              <Tooltip content="Navigate to next question">
                <span><kbd className="px-2 py-1 bg-white rounded border border-blue-300">→</kbd> Next</span>
              </Tooltip>
              <Tooltip content="Navigate to previous question">
                <span><kbd className="px-2 py-1 bg-white rounded border border-blue-300">←</kbd> Previous</span>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      {/* Question Card */}
      {currentQuestion && editedQuestion && (
        <div className="card space-y-6">
          {/* Metadata */}
          <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-xs text-gray-600">Question ID</p>
              <p className="font-mono text-sm font-medium">{currentQuestion.question_id}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Exam / Topic</p>
              <p className="text-sm font-medium">{currentQuestion.exam_id} / {currentQuestion.topic_id}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600">Type / Difficulty</p>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">{currentQuestion.question_type}</span>
                <span className="text-gray-400">/</span>
                <StatusBadge status={currentQuestion.difficulty} />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Quality Score</p>
              {currentQuestion.quality_score ? (
                <QualityBadge score={currentQuestion.quality_score} />
              ) : (
                <span className="text-sm text-gray-500">N/A</span>
              )}
            </div>
          </div>

          {/* Question Text */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">QUESTION:</label>
            {isEditing ? (
              <textarea
                value={editedQuestion.question_text}
                onChange={(e) => setEditedQuestion({ ...editedQuestion, question_text: e.target.value })}
                className="w-full min-h-24 input-field font-normal"
                rows={4}
              />
            ) : (
              <p className="text-gray-900 whitespace-pre-wrap">{currentQuestion.question_text}</p>
            )}
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">OPTIONS:</label>
            {Object.entries(currentQuestion.options).map(([key, value]: [string, any]) => (
              <div
                key={key}
                className={`p-4 rounded-lg border transition-colors ${
                  key === currentQuestion.correct_option
                    ? 'bg-green-50 border-green-200'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <span className="font-semibold text-gray-900 mt-1">{key})</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedQuestion.options[key as keyof typeof editedQuestion.options]}
                      onChange={(e) =>
                        setEditedQuestion({
                          ...editedQuestion,
                          options: { ...editedQuestion.options, [key]: e.target.value },
                        })
                      }
                      className="flex-1 input-field"
                    />
                  ) : (
                    <span className="flex-1 text-gray-900">{value as string}</span>
                  )}
                  {key === currentQuestion.correct_option && (
                    <span className="text-green-600 text-sm font-medium whitespace-nowrap">✓ CORRECT</span>
                  )}
                </div>
              </div>
            ))}

            {isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Correct Answer:</label>
                <select
                  value={editedQuestion.correct_option}
                  onChange={(e) => setEditedQuestion({ ...editedQuestion, correct_option: e.target.value })}
                  className="input-field w-48"
                >
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                </select>
              </div>
            )}
          </div>

          {/* Explanation */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">EXPLANATION:</label>
            {isEditing ? (
              <textarea
                value={editedQuestion.explanation}
                onChange={(e) => setEditedQuestion({ ...editedQuestion, explanation: e.target.value })}
                className="w-full min-h-32 input-field font-normal"
                rows={5}
              />
            ) : (
              <p className="text-gray-700 whitespace-pre-wrap">{currentQuestion.explanation}</p>
            )}
          </div>

          {/* Source Chunks */}
          {currentQuestion.source_chunk_ids && currentQuestion.source_chunk_ids.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">SOURCE CHUNKS:</label>
              <div className="flex flex-wrap gap-2">
                {currentQuestion.source_chunk_ids.map((chunkId: any) => (
                  <span key={chunkId} className="px-3 py-1 text-xs bg-gray-100 rounded-full font-mono">
                    {chunkId}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Auto-save indicator */}
          {isEditing && (
            <div className="text-xs text-gray-500 italic">
              Auto-saving draft...
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-6 border-t">
            {isEditing ? (
              <div className="flex space-x-3">
                <Tooltip content="Save changes to this question">
                  <button
                    onClick={handleSaveEdit}
                    disabled={updateMutation.isPending}
                    className="btn-primary"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </Tooltip>
                <Tooltip content="Discard changes and exit edit mode">
                  <button onClick={handleCancelEdit} className="btn-secondary">
                    Cancel
                  </button>
                </Tooltip>
              </div>
            ) : (
              <div className="flex space-x-3">
                <Tooltip content="Edit this question (E key)">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="btn-secondary flex items-center"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit (E)
                  </button>
                </Tooltip>
              </div>
            )}

            {!isEditing && (
              <div className="flex space-x-3">
                <Tooltip content="Reject this question with feedback (R key)">
                  <button
                    onClick={handleRejectClick}
                    disabled={rejectMutation.isPending}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject (R)
                  </button>
                </Tooltip>
                <Tooltip content="Approve and add to question bank (A key)">
                  <button
                    onClick={handleApprove}
                    disabled={approveMutation.isPending}
                    className="btn-primary flex items-center bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve (A)
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Review Progress</span>
          <span className="text-sm text-gray-600">
            {currentIndex + 1} / {questions.length} reviewed
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-600 transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Rejection Feedback Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Reject Question</h3>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionFeedback('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Important: Feedback for RAG System</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Your feedback will be used to improve future question generation. Please be specific about what was wrong with this question.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={rejectionFeedback}
                  onChange={(e) => setRejectionFeedback(e.target.value)}
                  className="w-full min-h-40 input-field"
                  rows={6}
                  placeholder="Describe why this question is being rejected (e.g., factually incorrect, unclear wording, wrong difficulty level, duplicate content, etc.)"
                  autoFocus
                />
              </div>

              <div className="text-xs text-gray-600 space-y-1">
                <p className="font-medium">Common rejection reasons:</p>
                <ul className="list-disc list-inside space-y-0.5 ml-2">
                  <li>Factually incorrect information</li>
                  <li>Unclear or ambiguous question wording</li>
                  <li>Incorrect answer or explanation</li>
                  <li>Duplicate or very similar to existing question</li>
                  <li>Wrong difficulty level for the topic</li>
                  <li>Poor quality distractors (incorrect options)</li>
                  <li>Missing or inadequate explanation</li>
                </ul>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectionFeedback('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectConfirm}
                  disabled={rejectMutation.isPending || !rejectionFeedback.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rejectMutation.isPending ? 'Rejecting...' : 'Reject & Send Feedback'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
